import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_1f8f562a from 'nuxt_plugin_sentryserver_1f8f562a' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_eb02393a from 'nuxt_plugin_sentryclient_eb02393a' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_recaptcha_36fc61d4 from 'nuxt_plugin_recaptcha_36fc61d4' // Source: ./recaptcha.js (mode: 'all')
import nuxt_plugin_pluginutils_4ac7b4f4 from 'nuxt_plugin_pluginutils_4ac7b4f4' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_6daed53f from 'nuxt_plugin_pluginrouting_6daed53f' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_11813690 from 'nuxt_plugin_pluginmain_11813690' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_googleanalytics_7c0e5d9f from 'nuxt_plugin_googleanalytics_7c0e5d9f' // Source: ./google-analytics.js (mode: 'client')
import nuxt_plugin_intersectionobserver_52293fe4 from 'nuxt_plugin_intersectionobserver_52293fe4' // Source: ../plugins/intersection-observer (mode: 'client')
import nuxt_plugin_resizeobserver_2fa00d99 from 'nuxt_plugin_resizeobserver_2fa00d99' // Source: ../plugins/resize-observer (mode: 'client')
import nuxt_plugin_adminrequest_791aec88 from 'nuxt_plugin_adminrequest_791aec88' // Source: ../plugins/admin-request.js (mode: 'all')
import nuxt_plugin_foliorequest_2d8d4ba2 from 'nuxt_plugin_foliorequest_2d8d4ba2' // Source: ../plugins/folio-request.js (mode: 'all')
import nuxt_plugin_isdev_322675e2 from 'nuxt_plugin_isdev_322675e2' // Source: ../plugins/is-dev.js (mode: 'all')
import nuxt_plugin_requireasset_21515be5 from 'nuxt_plugin_requireasset_21515be5' // Source: ../plugins/require-asset.js (mode: 'all')
import nuxt_plugin_globalcomponents_52ce1226 from 'nuxt_plugin_globalcomponents_52ce1226' // Source: ../plugins/global-components.js (mode: 'all')
import nuxt_plugin_trapdirective_012dc59c from 'nuxt_plugin_trapdirective_012dc59c' // Source: ../plugins/trap-directive.js (mode: 'client')
import nuxt_plugin_fadedirective_170c5b7a from 'nuxt_plugin_fadedirective_170c5b7a' // Source: ../plugins/fade-directive.js (mode: 'all')
import nuxt_plugin_gaevent_598e937a from 'nuxt_plugin_gaevent_598e937a' // Source: ../plugins/ga-event.js (mode: 'all')
import nuxt_plugin_fathomevent_de1643b2 from 'nuxt_plugin_fathomevent_de1643b2' // Source: ../plugins/fathom-event.js (mode: 'all')
import nuxt_plugin_focusmethoddirective_64b63cee from 'nuxt_plugin_focusmethoddirective_64b63cee' // Source: ../plugins/focus-method-directive.js (mode: 'all')
import nuxt_plugin_parallaxdirective_ec872bf0 from 'nuxt_plugin_parallaxdirective_ec872bf0' // Source: ../plugins/parallax-directive.js (mode: 'all')
import nuxt_plugin_reducedmotion_77f95a7c from 'nuxt_plugin_reducedmotion_77f95a7c' // Source: ../plugins/reduced-motion.js (mode: 'all')
import nuxt_plugin_portalvue_923e8108 from 'nuxt_plugin_portalvue_923e8108' // Source: ../plugins/portal-vue.js (mode: 'all')
import nuxt_plugin_alert_535fa891 from 'nuxt_plugin_alert_535fa891' // Source: ../plugins/alert.js (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Procreate® - Made for Artists","meta":[{"charset":"utf-8"},{"name":"robots","content":"index, follow"},{"http-equiv":"X-UA-Compatible","content":"IE=edge,chrome=1"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"Procreate is the most powerful and intuitive digital illustration app available for iPad. It's packed with features artists love, and it's an iPad exclusive."},{"property":"og:type","content":"website"},{"property":"og:site_name","content":"Procreate"},{"name":"twitter:site","content":"@procreate"}],"script":[{"src":"https:\u002F\u002Ftwenty-elegant.procreate.art\u002Fscript.js","data-site":"YVFEGLKW","data-spa":"auto","defer":true}],"link":[],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_1f8f562a === 'function') {
    await nuxt_plugin_sentryserver_1f8f562a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_eb02393a === 'function') {
    await nuxt_plugin_sentryclient_eb02393a(app.context, inject)
  }

  if (typeof nuxt_plugin_recaptcha_36fc61d4 === 'function') {
    await nuxt_plugin_recaptcha_36fc61d4(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_4ac7b4f4 === 'function') {
    await nuxt_plugin_pluginutils_4ac7b4f4(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_6daed53f === 'function') {
    await nuxt_plugin_pluginrouting_6daed53f(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_11813690 === 'function') {
    await nuxt_plugin_pluginmain_11813690(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googleanalytics_7c0e5d9f === 'function') {
    await nuxt_plugin_googleanalytics_7c0e5d9f(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_intersectionobserver_52293fe4 === 'function') {
    await nuxt_plugin_intersectionobserver_52293fe4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_resizeobserver_2fa00d99 === 'function') {
    await nuxt_plugin_resizeobserver_2fa00d99(app.context, inject)
  }

  if (typeof nuxt_plugin_adminrequest_791aec88 === 'function') {
    await nuxt_plugin_adminrequest_791aec88(app.context, inject)
  }

  if (typeof nuxt_plugin_foliorequest_2d8d4ba2 === 'function') {
    await nuxt_plugin_foliorequest_2d8d4ba2(app.context, inject)
  }

  if (typeof nuxt_plugin_isdev_322675e2 === 'function') {
    await nuxt_plugin_isdev_322675e2(app.context, inject)
  }

  if (typeof nuxt_plugin_requireasset_21515be5 === 'function') {
    await nuxt_plugin_requireasset_21515be5(app.context, inject)
  }

  if (typeof nuxt_plugin_globalcomponents_52ce1226 === 'function') {
    await nuxt_plugin_globalcomponents_52ce1226(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_trapdirective_012dc59c === 'function') {
    await nuxt_plugin_trapdirective_012dc59c(app.context, inject)
  }

  if (typeof nuxt_plugin_fadedirective_170c5b7a === 'function') {
    await nuxt_plugin_fadedirective_170c5b7a(app.context, inject)
  }

  if (typeof nuxt_plugin_gaevent_598e937a === 'function') {
    await nuxt_plugin_gaevent_598e937a(app.context, inject)
  }

  if (typeof nuxt_plugin_fathomevent_de1643b2 === 'function') {
    await nuxt_plugin_fathomevent_de1643b2(app.context, inject)
  }

  if (typeof nuxt_plugin_focusmethoddirective_64b63cee === 'function') {
    await nuxt_plugin_focusmethoddirective_64b63cee(app.context, inject)
  }

  if (typeof nuxt_plugin_parallaxdirective_ec872bf0 === 'function') {
    await nuxt_plugin_parallaxdirective_ec872bf0(app.context, inject)
  }

  if (typeof nuxt_plugin_reducedmotion_77f95a7c === 'function') {
    await nuxt_plugin_reducedmotion_77f95a7c(app.context, inject)
  }

  if (typeof nuxt_plugin_portalvue_923e8108 === 'function') {
    await nuxt_plugin_portalvue_923e8108(app.context, inject)
  }

  if (typeof nuxt_plugin_alert_535fa891 === 'function') {
    await nuxt_plugin_alert_535fa891(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
